import {
  CLIENT_LIFECYCLE,
  INDUSTRIES,
  LIFECYCLE_LABEL,
  LIFECYCLE_TONE,
  LINES_OF_BUSINESS,
  type ClientField,
  type ClientLifecycle,
  type ClientRecord as Rec,
  type CreateClient,
} from '@cyberscore/contracts';
import { CircleAlert, Loader2, Network } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { href, navigate } from '../../lib/router';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { useCanStart, useCreateAssessment } from '../assessments/api';
import { industryLabel } from '../assessments/labels';
import { bandLabel, bandTone } from '../portfolio/format';
import { useClient, useClientFields, useCreateClient, useUpdateClient } from './api';
import { StatusPill, when } from './parts';

/** Client records (task 66): the quick view on Clients, and the form to add a client. */

export function LifecyclePill({ stage }: { stage: ClientLifecycle }) {
  return <span className={`pill ${LIFECYCLE_TONE[stage]}`}>{LIFECYCLE_LABEL[stage]}</span>;
}

export const money = (n: number | null) =>
  n === null ? '–' : `RM ${n.toLocaleString('en-MY', { maximumFractionDigits: 2 })}`;
export const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '–'; // prettier-ignore

/** Days until a renewal date (negative once passed), in Malaysia. */
export const daysTo = (iso: string) => {
  const today = new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }));
  return Math.round((new Date(iso).getTime() - today.getTime()) / 86_400_000);
};
export function RenewalText({ date }: { date: string | null }) {
  if (!date) return <span className="muted">–</span>;
  const d = daysTo(date);
  return (
    <span className={d < 0 ? 'bad-t' : d <= 30 ? 'warn-t' : undefined} title={d < 0 ? `${-d} days ago` : `In ${d} days`}>
      {day(date)}
    </span>
  ); // prettier-ignore
}

// —— the form, shared by the quick view and New client ——

type BaseList = ['customer_name', 'lifecycle', 'policy_number', 'line_of_business', 'premium', 'sum_insured', 'renewal_date', 'broker', 'industry', 'country', 'parent_key']; // prettier-ignore
type Base = BaseList[number];
type Draft = Record<Base, string> & { custom: Record<string, string> };

const toDraft = (r: Partial<Rec> | null): Draft => ({
  customer_name: r?.customer_name ?? '',
  lifecycle: r?.lifecycle ?? 'prospect',
  policy_number: r?.policy_number ?? '',
  line_of_business: r?.line_of_business ?? '',
  premium: r?.premium == null ? '' : String(r.premium),
  sum_insured: r?.sum_insured == null ? '' : String(r.sum_insured),
  renewal_date: r?.renewal_date ?? '',
  broker: r?.broker ?? '',
  industry: r?.industry ?? '',
  country: r?.country ?? '',
  parent_key: r?.parent_key ?? '',
  custom: Object.fromEntries(Object.entries(r?.custom ?? {}).map(([k, v]) => [k, typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v)])), // prettier-ignore
});

/** The draft as the API takes it, or the errors to show. Money and numbers accept "12,500". */
function fromDraft(d: Draft): { body: Omit<CreateClient, 'customer_key'>; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const amount = (k: 'premium' | 'sum_insured') => {
    const t = d[k].replace(/[,\s]|^RM/gi, '');
    if (!t) return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) errors[k] = 'An amount in ringgit';
    return n;
  };
  if (!d.customer_name.trim()) errors.customer_name = 'Give the business’s name';
  const body = {
    customer_name: d.customer_name.trim(),
    lifecycle: d.lifecycle as ClientLifecycle,
    policy_number: d.policy_number.trim() || null,
    line_of_business: d.line_of_business.trim() || null,
    premium: amount('premium'),
    sum_insured: amount('sum_insured'),
    renewal_date: d.renewal_date || null,
    broker: d.broker.trim() || null,
    industry: (d.industry || null) as CreateClient['industry'],
    country: d.country.trim().toUpperCase() || null,
    parent_key: d.parent_key.trim() || null,
    custom: Object.fromEntries(Object.entries(d.custom).map(([k, v]) => [k, v === '' ? null : v === 'yes' ? true : v === 'no' ? false : v])), // prettier-ignore
  };
  if (body.country && !/^[A-Z]{2}$/.test(body.country)) errors.country = 'Two letters, e.g. MY';
  return { body, errors };
}

/** Server issues by field: "premium", "custom.segment". */
const issuesOf = (e: unknown) =>
  e instanceof ApiError ? Object.fromEntries(e.issues.map((i) => [i.path, i.message])) : {};

function Row({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: (id: string) => ReactNode;
}) {
  // prettier-ignore
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint && <span className="hint">{hint}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

function RecordForm({
  d,
  set,
  fields,
  errors,
  readOnly,
}: {
  d: Draft;
  set: (patch: Partial<Draft>) => void;
  fields: ClientField[];
  errors: Record<string, string>;
  readOnly: boolean;
}) {
  // prettier-ignore
  const text = (k: Base, label: string, extra: { hint?: string; type?: string; list?: string; mode?: 'decimal' } = {}) => (
    <Row label={label} error={errors[k]} hint={extra.hint}>
      {(id) => <input id={id} className={`input${errors[k] ? ' invalid' : ''}`} type={extra.type ?? 'text'} list={extra.list} inputMode={extra.mode} value={d[k]} disabled={readOnly} onChange={(e) => set({ [k]: e.target.value })} />}
    </Row>
  ); // prettier-ignore
  const lob = useId();
  return (
    <>
      {text('customer_name', 'Business name')}
      <div className="two">
        <Row label="Stage" error={errors.lifecycle}>
          {(id) => (
            <select id={id} className="input" value={d.lifecycle} disabled={readOnly} onChange={(e) => set({ lifecycle: e.target.value })}>
              {CLIENT_LIFECYCLE.map((s) => <option key={s} value={s}>{LIFECYCLE_LABEL[s]}</option>)}
            </select>
          )}
        </Row>
        {text('policy_number', 'Policy number')}
      </div>
      <div className="two">
        {text('premium', 'Premium (RM)', { mode: 'decimal' })}
        {text('sum_insured', 'Sum insured (RM)', { mode: 'decimal' })}
      </div>
      <div className="two">
        {text('renewal_date', 'Renewal date', { type: 'date' })}
        {text('line_of_business', 'Line of business', { list: lob })}
      </div>
      <datalist id={lob}>{LINES_OF_BUSINESS.map((l) => <option key={l} value={l} />)}</datalist>
      {text('broker', 'Broker')}
      <div className="two">
        <Row label="Industry" error={errors.industry}>
          {(id) => (
            <select id={id} className="input" value={d.industry} disabled={readOnly} onChange={(e) => set({ industry: e.target.value })}>
              <option value="">Not set</option>
              {Object.entries(INDUSTRIES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
        </Row>
        {text('country', 'Country', { hint: 'Two letters, e.g. MY' })}
      </div>
      {text('parent_key', 'Part of group', { hint: 'The parent company’s client key, when this client is a subsidiary.' })}
      {fields.map((f) => {
        const v = d.custom[f.key] ?? '';
        const err = errors[`custom.${f.key}`];
        const put = (x: string) => set({ custom: { ...d.custom, [f.key]: x } });
        return (
          <Row key={f.key} label={f.label} error={err} hint={f.help || undefined}>
            {(id) =>
              f.type === 'choice' || f.type === 'boolean' ? (
                <select id={id} className={`input${err ? ' invalid' : ''}`} value={v} disabled={readOnly} onChange={(e) => put(e.target.value)}>
                  <option value="">Not set</option>
                  {(f.type === 'boolean' ? [['yes', 'Yes'], ['no', 'No']] : f.options.map((o) => [o, o])).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              ) : (
                <input id={id} className={`input${err ? ' invalid' : ''}`} type={f.type === 'date' ? 'date' : 'text'} inputMode={f.type === 'number' || f.type === 'currency' ? 'decimal' : undefined} value={v} disabled={readOnly} onChange={(e) => put(e.target.value)} />
              )
            }
          </Row>
        );
      })}
    </>
  ); // prettier-ignore
}

function Banner({ children }: { children: ReactNode }) {
  return (
    <div className="result bad" role="alert">
      <CircleAlert size={16} />
      <span>{children}</span>
    </div>
  );
}

// —— the quick view ——

type Detail = NonNullable<ReturnType<typeof useClient>['data']>;

/** One client at a glance, from Clients: its record (editable for staff and brokers) and its assessments. */
export function ClientQuickView({ customerKey, onClose }: { customerKey: string; onClose: () => void }) {
  const q = useClient(customerKey);
  if (!q.data)
    return (
      <Drawer title="Client" onClose={onClose} footer={<button type="button" className="btn" onClick={onClose}>Close</button>}>
        {q.isError ? <Banner>{q.error.message}</Banner> : <div className="empty">Loading…</div>}
      </Drawer>
    ); // prettier-ignore
  return <QuickView key={`${q.data.record.revision}`} detail={q.data} refetch={() => void q.refetch()} onClose={onClose} />; // prettier-ignore
}

/** The record's draft and saving it, shared by the quick view and Client 360's Record tab. */
function useRecordEditor(detail: Detail, refetch: () => void) {
  const r = detail.record;
  const save = useUpdateClient(r.customer_key);
  const toast = useToast();
  const start = toDraft(r);
  const [d, setD] = useState(start);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<ReactNode>(null);
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  const changed = JSON.stringify(d) !== JSON.stringify(start);

  const submit = async () => {
    const { body, errors: e } = fromDraft(d);
    setErrors(e);
    if (Object.keys(e).length) return;
    const was = fromDraft(start).body;
    const patch = Object.fromEntries(Object.entries(body).filter(([k, v]) => k !== 'custom' && JSON.stringify(v) !== JSON.stringify(was[k as keyof typeof was]))); // prettier-ignore
    const custom = Object.fromEntries(Object.entries(body.custom ?? {}).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(was.custom?.[k] ?? null))); // prettier-ignore
    setBanner(null);
    try {
      await save.mutateAsync({
        ...patch,
        ...(Object.keys(custom).length ? { custom } : {}),
        revision: r.revision,
      });
      toast(`${body.customer_name} saved.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409)
        setBanner(<>{err.message} <button type="button" className="linklike" onClick={refetch}>Reload</button></>); // prettier-ignore
      else {
        setErrors(issuesOf(err));
        setBanner(err instanceof ApiError ? err.message : 'Could not save. Try again.');
      }
    }
  };
  return { d, set, errors, banner, changed, submit, saving: save.isPending };
}

function QuickView({
  detail,
  refetch,
  onClose,
}: {
  detail: Detail;
  refetch: () => void;
  onClose: () => void;
}) {
  const { record: r, fields, assessments, can_edit } = detail;
  const { d, set, errors, banner, changed, submit, saving } = useRecordEditor(detail, refetch);
  const [adding, setAdding] = useState(false);

  if (adding) return <EnvironmentDrawer detail={detail} onClose={() => setAdding(false)} />;
  return (
    <Drawer
      title={r.customer_name}
      subtitle={`${LIFECYCLE_LABEL[r.lifecycle]} · ${r.customer_key}${r.updated_by_name && r.updated_at ? ` · saved by ${r.updated_by_name}, ${when(r.updated_at)}` : ''}`}
      onClose={onClose}
      footer={
        <>
          <a className="btn" href={href.client(r.customer_key)}>Open Client 360</a>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Close</button>
          {can_edit && (
            <button type="button" className="btn btn-primary" onClick={submit} disabled={!changed || saving}>
              {saving && <Loader2 size={15} className="spin" />}
              Save changes
            </button>
          )}
        </>
      }
    >
      {banner && <Banner>{banner}</Banner>}
      <section className="qv-assessments" aria-label="Assessments">
        <div className="eyebrow">Assessments</div>
        {assessments.length ? (
          <ul className="plain-list">
            {assessments.slice(0, 5).map((a) => (
              <li key={a.id} className="row-between">
                <a href={href.score(a.id)}>{a.title}{a.environment && <span className="muted"> · {a.environment}</span>}</a>
                <span className="qv-meta">
                  {a.score !== null && a.band && <><b className="num">{a.score}</b> <span className={`pill ${bandTone(a.band as never)}`}>{bandLabel(a.band as never)}</span></>}
                  <StatusPill status={a.status as never} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">No assessment yet. Start one from Assessments with the key {r.customer_key}.</p>
        )}
      </section>
      <GroupSection detail={detail} onAdd={() => setAdding(true)} />
      <RecordForm d={d} set={set} fields={fields} errors={errors} readOnly={!can_edit} />
      {!can_edit && <p className="muted small">You can see this client’s record. Underwriters, brokers and admins change it.</p>}
      {!r.revision && can_edit && <p className="muted small">Nothing has been saved for this client yet: what shows comes from its latest assessment ({industryLabel(r.industry)}).</p>}
    </Drawer>
  ); // prettier-ignore
}

// —— Client 360's Record tab (task 100) ——

/** The record's fields, editable for staff and brokers, as a card rather than a drawer. */
export function ClientRecordCard({ detail, refetch }: { detail: Detail; refetch: () => void }) {
  const { record: r, fields, can_edit } = detail;
  const { d, set, errors, banner, changed, submit, saving } = useRecordEditor(detail, refetch);
  return (
    <section className="card pad stack" aria-labelledby="client-record-h">
      <div className="row-between">
        <h2 id="client-record-h" className="section-title">Client record</h2>
        {r.updated_by_name && r.updated_at && <span className="muted small">Saved by {r.updated_by_name}, {when(r.updated_at)}</span>}
      </div>
      {banner && <Banner>{banner}</Banner>}
      <RecordForm d={d} set={set} fields={fields} errors={errors} readOnly={!can_edit} />
      {!can_edit && <p className="muted small">You can see this client’s record. Underwriters, brokers and admins change it.</p>}
      {can_edit && (
        <div className="row gap">
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!changed || saving}>
            {saving && <Loader2 size={15} className="spin" />}
            Save changes
          </button>
        </div>
      )}
    </section>
  ); // prettier-ignore
}

/** The client's group and IT environments, with adding one, for Client 360. */
export function ClientGroup({ detail }: { detail: Detail }) {
  const [adding, setAdding] = useState(false);
  return (
    <>
      <GroupSection detail={detail} onAdd={() => setAdding(true)} />
      {adding && <EnvironmentDrawer detail={detail} onClose={() => setAdding(false)} />}
    </>
  );
}

// —— group companies (task 94) ——

/**
 * The client's group: its parent or its subsidiaries, and its IT environments. A group with more than one IT
 * environment is assessed once per environment, each on its own insurers' forms (decision 0071).
 */
function GroupSection({ detail, onAdd }: { detail: Detail; onAdd: () => void }) {
  const { record: r, group, assessments } = detail;
  const canStart = useCanStart();
  const environments = assessments.filter((a) => a.environment);
  if (!group.parent && !group.subsidiaries.length && !environments.length && !canStart) return null;
  const find = (key: string) => `${href.clients()}?q=${encodeURIComponent(key)}`;
  return (
    <section className="qv-group" aria-label="Group">
      <div className="eyebrow">Group</div>
      {group.parent ? (
        <p className="small">Subsidiary of <a href={find(group.parent.key)}>{group.parent.name}</a></p>
      ) : group.subsidiaries.length ? (
        <p className="small">
          Parent of {group.subsidiaries.map((s, i) => <span key={s.key}>{i ? ', ' : ''}<a href={find(s.key)}>{s.name}</a></span>)}
        </p>
      ) : (
        <p className="muted small">Not part of a group. Set “Part of group” on a subsidiary’s record to link it here.</p>
      )}
      {environments.length > 0 && (
        <ul className="plain-list small" aria-label="IT environments">
          {environments.map((a) => (
            <li key={a.id}>
              <a href={href.assessment(a.id)}>{a.environment}</a>
              {a.entities.length > 0 && <span className="muted"> · {a.entities.map((k) => k === r.customer_key ? r.customer_name : (group.subsidiaries.find((s) => s.key === k)?.name ?? k)).join(', ')}</span>}
            </li>
          ))}
        </ul>
      )}
      {canStart && !group.parent && (
        <button type="button" className="btn btn-sm" onClick={onAdd}>
          <Network size={14} /> Add an IT environment
        </button>
      )}
    </section>
  ); // prettier-ignore
}

/** A new assessment for one IT environment of the group, with the company's facts copied from another. */
function EnvironmentDrawer({ detail, onClose }: { detail: Detail; onClose: () => void }) {
  const { record: r, group, assessments } = detail;
  const create = useCreateAssessment();
  const toast = useToast();
  const companies = [{ key: r.customer_key, name: r.customer_name }, ...group.subsidiaries];
  const [name, setName] = useState('');
  const [entities, setEntities] = useState<string[]>([]);
  const [copyFrom, setCopyFrom] = useState(assessments[0]?.id ?? '');
  const [industry, setIndustry] = useState(r.industry ?? 'manufacturing');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const ids = { name: useId(), from: useId(), industry: useId() };

  const submit = async () => {
    if (!name.trim()) return setErrors({ name: 'Name the IT environment, e.g. “Head office”' });
    setErrors({});
    setBanner(null);
    try {
      const a = (await create.mutateAsync({
        title: `Cyber check: ${name.trim()}`,
        customer: r.customer_key,
        customer_name: r.customer_name,
        country: r.country ?? 'MY',
        industry,
        environment: { name: name.trim(), entities, ...(copyFrom ? { copy_from: copyFrom } : {}) },
      })) as { id: string; copied?: string[] };
      toast(a.copied?.length ? `${name.trim()} started, with ${a.copied.length} of the company’s answers to confirm.` : `${name.trim()} started.`); // prettier-ignore
      navigate(href.assessment(a.id));
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(Object.fromEntries(err.issues.map((i) => [i.path.split('.').pop()!, i.message])));
        setBanner(err.message);
      } else setBanner('Could not start the assessment. Try again.');
    }
  };

  return (
    <Drawer
      title="Add an IT environment"
      subtitle={`${r.customer_name}: assessed and proposed to insurers on its own.`}
      onClose={onClose}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={create.isPending}>
            {create.isPending && <Loader2 size={15} className="spin" />}
            Start the assessment
          </button>
        </>
      }
    >
      {banner && <Banner>{banner}</Banner>}
      <div className="field">
        <label htmlFor={ids.name}>Name</label>
        <input id={ids.name} className={`input${errors.name ? ' invalid' : ''}`} value={name} maxLength={120} autoFocus onChange={(e) => setName(e.target.value)} />
        <span className="hint">A network the group runs separately, e.g. “Head office” or “Plantation estates”.</span>
        {errors.name && <span className="error">{errors.name}</span>}
      </div>
      <fieldset className="field checks">
        <legend>Companies on it</legend>
        {companies.map((c) => (
          <label key={c.key} className="toggle-row small">
            <input type="checkbox" checked={entities.includes(c.key)} onChange={(e) => setEntities((x) => (e.target.checked ? [...x, c.key] : x.filter((k) => k !== c.key)))} />
            {c.name}
          </label>
        ))}
        {errors.entities && <span className="error">{errors.entities}</span>}
      </fieldset>
      <div className="field">
        <label htmlFor={ids.from}>Copy the company’s facts from</label>
        <select id={ids.from} className="input" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
          <option value="">Don’t copy</option>
          {assessments.map((a) => <option key={a.id} value={a.id}>{a.title}{a.environment ? ` · ${a.environment}` : ''}</option>)}
        </select>
        <span className="hint">Registration, address, activities, the group and the insurers chosen: the client confirms them.</span>
        {errors.copy_from && <span className="error">{errors.copy_from}</span>}
      </div>
      <div className="field">
        <label htmlFor={ids.industry}>Industry</label>
        <select id={ids.industry} className="input" value={industry} onChange={(e) => setIndustry(e.target.value)}>
          {Object.entries(INDUSTRIES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
    </Drawer>
  ); // prettier-ignore
}

// —— adding a client ——

const slug = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/** A client before its first assessment, usually a prospect. */
export function NewClientDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (key: string) => void;
}) {
  const fields = useClientFields();
  const create = useCreateClient();
  const toast = useToast();
  const [d, setD] = useState(toDraft(null));
  const [key, setKey] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const set = (p: Partial<Draft>) => {
    setD((x) => ({ ...x, ...p }));
    if (p.customer_name !== undefined && !keyEdited) setKey(slug(p.customer_name));
  };

  const submit = async () => {
    const { body, errors: e } = fromDraft(d);
    if (!key.trim()) e.customer_key = 'Give the client a key';
    setErrors(e);
    if (Object.keys(e).length) return;
    setBanner(null);
    try {
      const r = await create.mutateAsync({ ...body, customer_key: key.trim(), custom: Object.fromEntries(Object.entries(body.custom ?? {}).filter(([, v]) => v !== null)) }); // prettier-ignore
      toast(`${r.customer_name} added.`);
      onCreated(r.customer_key);
    } catch (err) {
      setErrors(issuesOf(err));
      setBanner(err instanceof ApiError ? err.message : 'Could not add the client. Try again.');
    }
  };

  return (
    <Drawer
      title="New client"
      subtitle="A business you work with, before or alongside its assessments."
      onClose={onClose}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={create.isPending}>
            {create.isPending && <Loader2 size={15} className="spin" />}
            Add client
          </button>
        </>
      }
    >
      {banner && <Banner>{banner}</Banner>}
      <RecordForm d={d} set={set} fields={fields.data ?? []} errors={errors} readOnly={false} />
      <Row label="Client key" error={errors.customer_key} hint="How assessments find this client: use the same key when you start one.">
        {(id) => <input id={id} className={`input mono${errors.customer_key ? ' invalid' : ''}`} value={key} maxLength={200} onChange={(e) => { setKey(e.target.value); setKeyEdited(true); }} />}
      </Row>
    </Drawer>
  ); // prettier-ignore
}
